import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { crmFollowupsAPI, userAPI, agencyAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * CreateFollowupDialog — used from a CRM note (source_type=crm_note),
 * a contract-request (source_type=contract_request), or a contract note
 * (source_type=contract_note with source_parent_id).
 *
 * Assignee picker: type-ahead over users + a "+ Agency" chip that resolves
 * to the current subject's agency if provided.
 */
export function CreateFollowupDialog({
  open, onOpenChange, source, defaultAssigneeAgencyId = null,
  onCreated,
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [assignees, setAssignees] = useState([]); // [{kind,id,display_name}]
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    // Load users once when dialog opens; filter client-side.
    userAPI.list().then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setAllUsers(rows);
    }).catch(() => {});
  }, [open]);

  React.useEffect(() => {
    if (!userSearch || userSearch.length < 2) { setUserResults([]); return; }
    const q = userSearch.toLowerCase();
    const filtered = allUsers.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''} ${u.name || ''}`.toLowerCase();
      return name.includes(q);
    }).slice(0, 10);
    setUserResults(filtered);
  }, [userSearch, allUsers]);

  const addUser = (u) => {
    if (assignees.find((a) => a.kind === 'user' && a.id === u.user_id)) return;
    setAssignees([...assignees, {
      kind: 'user', id: u.user_id,
      display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
    }]);
    setUserSearch('');
    setUserResults([]);
  };
  const addAgency = async () => {
    if (!defaultAssigneeAgencyId) {
      toast.error('No agency in scope for this profile');
      return;
    }
    if (assignees.find((a) => a.kind === 'agency' && a.id === defaultAssigneeAgencyId)) return;
    let name = 'Agency';
    try {
      const { data } = await agencyAPI.get(defaultAssigneeAgencyId);
      name = data.agency_name || 'Agency';
    } catch { /* ignore */ }
    setAssignees([...assignees, { kind: 'agency', id: defaultAssigneeAgencyId, display_name: name }]);
  };
  const removeAssignee = (idx) => setAssignees(assignees.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!title.trim() || title.trim().length < 3) return toast.error('Title required (min 3 chars)');
    if (!dueDate) return toast.error('Due date required');
    if (!assignees.length) return toast.error('Add at least one assignee');
    setSaving(true);
    try {
      const payload = {
        source_type: source.source_type,
        source_id: source.source_id,
        source_parent_id: source.source_parent_id || undefined,
        subject_type: source.subject_type,
        subject_id: source.subject_id,
        title: title.trim(),
        description: description.trim() || undefined,
        assignees: assignees.map(({ kind, id }) => ({ kind, id })),
        due_date: dueDate,
        reminder_date: reminderDate || undefined,
      };
      const { data } = await crmFollowupsAPI.create(payload);
      toast.success('Follow-up created');
      onOpenChange(false);
      setTitle(''); setDescription(''); setDueDate(''); setReminderDate(''); setAssignees([]);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create follow-up');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent data-testid="create-followup-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Follow-up</DialogTitle>
          <DialogDescription>
            Assign work to one or more people. Follow-ups can be tracked from the dashboard queue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   data-testid="create-followup-title"
                   placeholder="e.g. Contact agent for updated license" />
          </div>

          <div>
            <Label>Instructions / Description</Label>
            <Textarea rows={4} value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What needs to be done? Assignees see this — the source note stays private."
                      data-testid="create-followup-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due date *</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                     data-testid="create-followup-due" />
            </div>
            <div>
              <Label>Reminder date (optional)</Label>
              <Input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)}
                     data-testid="create-followup-reminder" />
            </div>
          </div>

          <div data-testid="create-followup-assignees">
            <Label>Assignees *</Label>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {assignees.map((a, i) => (
                <Badge key={i} variant="outline" className="flex items-center gap-1">
                  {a.kind === 'agency' ? '🏢' : '👤'} {a.display_name}
                  <button type="button" onClick={() => removeAssignee(i)}><X size={12} /></button>
                </Badge>
              ))}
              {defaultAssigneeAgencyId && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={addAgency}
                        data-testid="create-followup-add-agency">
                  + Assign to Agency
                </Button>
              )}
            </div>
            <Input placeholder="Search users by name or email…"
                   value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                   data-testid="create-followup-user-search" />
            {userResults.length > 0 && (
              <div className="border rounded-md mt-1 max-h-48 overflow-y-auto bg-white">
                {userResults.map((u) => (
                  <button
                    key={u.user_id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                    onClick={() => addUser(u)}
                  >
                    <span className="font-medium">{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</span>
                    <span className="text-slate-500 ml-2">{u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="create-followup-submit">
            {saving ? 'Creating…' : 'Create Follow-up'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateFollowupDialog;
